import type {
  ImageComponent,
  LeafPrimitive,
  LocalizedRichTextComponent,
  LocalizedTextComponent,
  PrinterProfile,
  ResolvedImage,
  SymbolComponent,
  TextComponent,
  TextValue,
  VariableMeta,
  Warning,
} from '@tsagko/label-contracts'
import { SYMBOLOGY_METRICS } from '@tsagko/label-contracts'
import type { FlatZone } from './flatten-zones'
import { escapeZplText } from './escape-zpl-text'
import {
  A0_VMETRICS,
  barcodeIntrinsicSize,
  capBandAnchorY,
  DEFAULT_PLACEMENT,
  hAlignToPlan,
  resolveAnchor,
  textIntrinsicSize,
  rotationToPlan,
  textPlanGeometry,
} from './internal/intrinsic-size'
import { mmToDots, resolveValueSource } from './internal/units'
import { buildRenderPlan, type RenderPlan } from './render-plan'

/**
 * Legacy `ValueSource` shape. See `internal/units.ts` for the full
 * compatibility story.
 */
type LegacyStatic = { kind: 'static'; value: string }
type LegacyBound = { kind: 'bound'; fieldKey: string }
type AnyValueSource = TextValue | LegacyStatic | LegacyBound

/**
 * Regex matching a TextValue content string that is a single `{name}`
 * reference. Mirrors `variables.ts#SINGLE_VARIABLE_RE`.
 */
const SINGLE_VARIABLE_RE = /^\s*\{([a-zA-Z][a-zA-Z0-9_]*)\}\s*$/

/**
 * Authoring-placeholder shape returned by `pickPlaceholder` when a text-value
 * carrier resolves to empty. Distinguishes three cases the planner cares
 * about:
 *   - `kind: 'skip'`   → pure-literal empty value; no plan emitted.
 *   - `kind: 'bound'`  → reference to a runtime data slot; synthesise the
 *                        placeholder text (`{name}` for new shape,
 *                        `{fieldKey}` for legacy) so the designer canvas has
 *                        something selectable.
 * (Non-empty resolved values bypass this helper entirely.)
 */
type Placeholder = { kind: 'skip' } | { kind: 'bound'; label: string }

/**
 * Classify a text-value carrier when its resolved string is empty. Empty
 * resolution can mean:
 *   - the legacy `{ kind: 'static', value: '' }` case → skip.
 *   - the legacy `{ kind: 'bound', fieldKey: 'x' }` with no data → synth `{x}`.
 *   - the new `TextValue { content: '' }` (pure literal empty) → skip.
 *   - the new `TextValue { content: '{x}' }` with no data and no default
 *     → synth `{x}`.
 */
function pickPlaceholder(source: AnyValueSource): Placeholder {
  if ('content' in source) {
    const match = SINGLE_VARIABLE_RE.exec(source.content)
    if (!match) return { kind: 'skip' }
    // `match[1]` is the variable name (non-empty by the regex's character
    // class). Render `{name}` as the placeholder label.
    return { kind: 'bound', label: source.content.trim() }
  }
  if (source.kind === 'bound') return { kind: 'bound', label: `{${source.fieldKey}}` }
  return { kind: 'skip' }
}

const PT_TO_MM = 25.4 / 72

/**
 * Authoring-mode planner. Same input shape as `buildRenderPlan` but never
 * silently skips a bound primitive: empty resolved values become
 * `{fieldKey}` placeholders so the designer canvas always shows something
 * for the user to select and edit.
 *
 * Output is NOT consumable by `commandsFromPlans` — a runtime guard there
 * throws on any plan with `placeholderLabel` set. (This task adds only the
 * text-family arms which use synthesized text rather than placeholderLabel;
 * Tasks 3 and 4 will add image/symbol arms that DO set placeholderLabel.)
 *
 * Pass-through strategy: To keep tasks 3 and 4 to one-switch-arm changes
 * each, `buildAuthoringPlan` calls `buildRenderPlan` once and replays its
 * output for primitive kinds we haven't diverged on yet (separator/image/
 * symbol). Plans get indexed back to their source zone via the rule that
 * plan ids are either the primitive id or `${componentId}:variant:<i>` —
 * splitting on `:variant:` recovers the originating component id.
 */
export function buildAuthoringPlan(
  flatZones: FlatZone[],
  data: Record<string, unknown>,
  profile: PrinterProfile,
  resolvedImages: Map<string, ResolvedImage>,
  variables: Record<string, VariableMeta> = {}
): { plans: RenderPlan[]; warnings: Warning[] } {
  const plans: RenderPlan[] = []
  const warnings: Warning[] = []

  // Compute the print-mode plan once so the pass-through arms can replay
  // its output without re-implementing separator/image/symbol logic. Tasks
  // 3 and 4 will peel off the image and symbol arms; the separator arm
  // stays a pass-through for the foreseeable future (its print output IS
  // the authoring output — separators carry no data binding).
  //
  // We index by component id (unique per template by contract). FlatZone
  // has no id of its own; the component is the natural key. Plan ids are
  // `<componentId>`, `<componentId>:variant:<i>` for localized-rich-text
  // expansion, or `<componentId>:hri` for the barcode human-readable line —
  // strip both markers to recover the originating component id.
  //
  // Assumption: no component id contains ':variant:' or ends in ':hri'. The
  // LeafPrimitive schema doesn't enforce this; if it ever becomes an issue,
  // add a lint rule or use a structural marker instead.
  const printResult = buildRenderPlan(flatZones, data, profile, resolvedImages, variables)
  const printPlansByComponentId = new Map<string, RenderPlan[]>()
  for (const zone of flatZones) printPlansByComponentId.set(zone.component.id, [])
  for (const plan of printResult.plans) {
    const sourceId = plan.id.split(':variant:')[0]!.replace(/:hri$/, '')
    const bucket = printPlansByComponentId.get(sourceId)
    if (bucket) bucket.push(plan)
  }
  warnings.push(...printResult.warnings)

  for (const zone of flatZones) {
    const c: LeafPrimitive = zone.component
    switch (c.type) {
      case 'text': {
        const r = planAuthoringText(zone, c, data, profile, variables)
        plans.push(...r.plans)
        warnings.push(...r.warnings)
        break
      }
      case 'localized-text': {
        const r = planAuthoringLocalizedText(zone, c, data, profile, variables)
        plans.push(...r.plans)
        warnings.push(...r.warnings)
        break
      }
      case 'localized-rich-text': {
        const r = planAuthoringLocalizedRichText(zone, c, data, profile, variables)
        plans.push(...r.plans)
        warnings.push(...r.warnings)
        break
      }
      case 'symbol': {
        // Reuse the pre-computed print plans for this zone instead of
        // re-invoking buildRenderPlan per symbol — the parent already
        // accumulated its warnings at the top of this function. The bucket
        // may hold TWO plans (barcode + its `:hri` text line).
        const printPlans = printPlansByComponentId.get(c.id) ?? []
        const r = planAuthoringSymbol(zone, c, profile, printPlans)
        plans.push(...r.plans)
        warnings.push(...r.warnings)
        break
      }
      case 'image': {
        // Reuse the pre-computed print plan for this zone instead of
        // re-deriving image-ref resolution — the parent already accumulated
        // any warnings at the top of this function.
        const printPlan = printPlansByComponentId.get(c.id)?.[0]
        const r = planAuthoringImage(zone, c, printPlan)
        plans.push(...r.plans)
        warnings.push(...r.warnings)
        break
      }
      case 'separator': {
        // Separator carries no data binding — print output IS the authoring
        // output. Replay verbatim from the pre-computed print plan.
        const carried = printPlansByComponentId.get(c.id) ?? []
        plans.push(...carried)
        break
      }
      default: {
        const _ex: never = c
        throw new Error(`buildAuthoringPlan: unknown primitive ${JSON.stringify(_ex)}`)
      }
    }
  }
  return { plans, warnings }
}

/**
 * Authoring counterpart of `planText` (in `render-plan.ts`). Always emits
 * one plan: empty resolved value → `{fieldKey}` placeholder text.
 */
function planAuthoringText(
  zone: FlatZone,
  c: TextComponent,
  data: Record<string, unknown>,
  profile: PrinterProfile,
  variables: Record<string, VariableMeta>
): { plans: RenderPlan[]; warnings: Warning[] } {
  const value = c.value as AnyValueSource
  const resolved = resolveValueSource(value, data, variables)
  const sizePt = c.sizePt ?? 10
  const fontHeightMm = sizePt * PT_TO_MM
  const maxLines = c.maxLines ?? 1
  let text: string
  let warnings: Warning[] = []
  if (resolved.length === 0) {
    const placeholder = pickPlaceholder(value)
    if (placeholder.kind === 'skip') {
      // Pure-literal empty value: no synthesis. Synthesising
      // `{<componentId>}` exposes internal nanoid-style IDs on the canvas,
      // which is worse UX than the Layer 1 zone outline + Properties panel
      // that the user already has as the editing affordance. Symmetric with
      // `buildRenderPlan`'s silent-skip behaviour.
      return { plans: [], warnings: [] }
    }
    // Bound-empty: surface the variable reference as the placeholder label so
    // the canvas has something selectable. New shape preserves `{name}` from
    // the authored content; legacy shape synthesises `{fieldKey}`.
    text = placeholder.label
  } else {
    const escaped = escapeZplText(resolved, profile.encoding)
    text = escaped.value
    warnings = escaped.warnings
  }
  const placement = c.placement ?? DEFAULT_PLACEMENT
  const rotation = rotationToPlan(c.rotation)
  // Anchor + ^FB letter/width + canvas pin, rotation-aware (issue #1).
  // Mirrors planText in render-plan.ts via the shared textPlanGeometry.
  const geo = textPlanGeometry({
    region: { xMm: zone.xMm, yMm: zone.yMm, wMm: zone.widthMm, hMm: zone.heightMm },
    fontHeightMm,
    placement,
    rotation,
    lines: maxLines,
    lineSpacingMm: 0,
  })
  return {
    plans: [
      {
        kind: 'text',
        id: c.id,
        anchorXMm: geo.anchorXMm,
        anchorYMm: geo.anchorYMm,
        text,
        fontHeightMm,
        fontWidthMm: fontHeightMm / 2,
        fontId: c.fontId ?? '0',
        alignment: geo.alignment,
        wrapBoxWidthMm: geo.wrapBoxWidthMm,
        maxLines,
        lineSpacingMm: 0,
        rotation,
        vAlign: geo.vAlign,
        regionXMm: zone.xMm,
        regionYMm: zone.yMm,
        regionWidthMm: zone.widthMm,
        regionHeightMm: zone.heightMm,
        regionBackgroundColor: zone.backgroundColor,
      },
    ],
    warnings,
  }
}

/**
 * Picks the active variant for `planAuthoringLocalizedText`. Behaviour is
 * a 1:1 copy of `render-plan.ts#pickLocalizedVariant` (kept local because
 * that helper is not exported).
 */
function pickLocalizedVariant<T extends { locale: string }>(
  variants: readonly T[],
  data: Record<string, unknown>
): T {
  const candidate =
    typeof data._locale === 'string'
      ? data._locale
      : typeof data.locale === 'string'
        ? data.locale
        : undefined
  if (candidate !== undefined) {
    const match = variants.find(v => v.locale === candidate)
    if (match) return match
  }
  return variants[0]!
}

/**
 * Authoring counterpart of `planLocalizedText`. Always emits one plan
 * (active variant), with `{fieldKey}` synthesized on empty resolution.
 */
function planAuthoringLocalizedText(
  zone: FlatZone,
  c: LocalizedTextComponent,
  data: Record<string, unknown>,
  profile: PrinterProfile,
  variables: Record<string, VariableMeta>
): { plans: RenderPlan[]; warnings: Warning[] } {
  const variant = pickLocalizedVariant(c.variants, data)
  const variantValue = variant.value as AnyValueSource
  const resolved = resolveValueSource(variantValue, data, variables)
  const sizePt = c.sizePt ?? 10
  const fontHeightMm = sizePt * PT_TO_MM
  let text: string
  let warnings: Warning[] = []
  if (resolved.length === 0) {
    const placeholder = pickPlaceholder(variantValue)
    if (placeholder.kind === 'skip') {
      // Pure-literal empty picked variant: skip synthesis (see
      // `planAuthoringText` for rationale).
      return { plans: [], warnings: [] }
    }
    text = placeholder.label
  } else {
    const escaped = escapeZplText(resolved, profile.encoding)
    text = escaped.value
    warnings = escaped.warnings
  }
  const placement = c.placement ?? DEFAULT_PLACEMENT
  const rotation = rotationToPlan(c.rotation)
  // Anchor + ^FB letter/width + canvas pin, rotation-aware (issue #1).
  // Mirrors planLocalizedText in render-plan.ts via the shared helper.
  const geo = textPlanGeometry({
    region: { xMm: zone.xMm, yMm: zone.yMm, wMm: zone.widthMm, hMm: zone.heightMm },
    fontHeightMm,
    placement,
    rotation,
  })
  return {
    plans: [
      {
        kind: 'text',
        id: c.id,
        anchorXMm: geo.anchorXMm,
        anchorYMm: geo.anchorYMm,
        text,
        fontHeightMm,
        fontWidthMm: fontHeightMm / 2,
        fontId: c.fontId ?? '0',
        alignment: geo.alignment,
        wrapBoxWidthMm: geo.wrapBoxWidthMm,
        maxLines: 1,
        lineSpacingMm: 0,
        rotation,
        vAlign: geo.vAlign,
        regionXMm: zone.xMm,
        regionYMm: zone.yMm,
        regionWidthMm: zone.widthMm,
        regionHeightMm: zone.heightMm,
        regionBackgroundColor: zone.backgroundColor,
      },
    ],
    warnings,
  }
}

/**
 * Authoring counterpart of `planLocalizedRichText`. Always emits one plan
 * per declared variant (including empty ones), with `{fieldKey}` synthesized
 * on empty resolution. Y-stride / column-width math mirrors print mode so
 * authoring layout matches what the user will see in print.
 */
function planAuthoringLocalizedRichText(
  zone: FlatZone,
  c: LocalizedRichTextComponent,
  data: Record<string, unknown>,
  profile: PrinterProfile,
  variables: Record<string, VariableMeta>
): { plans: RenderPlan[]; warnings: Warning[] } {
  const sizePt = c.sizePt ?? 10
  const fontHeightMm = sizePt * PT_TO_MM
  const layout = c.layout ?? 'stacked'
  const lineHeightMm = sizePt * 0.4
  const placement = c.placement ?? DEFAULT_PLACEMENT
  const columnWidthMm =
    layout === 'columns' && c.variants.length > 0 ? zone.widthMm / c.variants.length : 0
  const plans: RenderPlan[] = []
  const warnings: Warning[] = []
  c.variants.forEach((variant, index) => {
    const variantValue = variant.value as AnyValueSource
    const resolved = resolveValueSource(variantValue, data, variables)
    let text: string
    if (resolved.length === 0) {
      // Pure-literal empty variants are skipped (no plan emitted for that
      // variant index) — see `planAuthoringText` for rationale. Bound /
      // variable-referencing variants still synthesise their placeholder so
      // the bound slot is visible. We keep `index` as the original variant
      // index so the y-stride math stays correct: bound-empty + resolved
      // siblings sit at their declared slot.
      const placeholder = pickPlaceholder(variantValue)
      if (placeholder.kind === 'skip') return
      text = placeholder.label
    } else {
      const escaped = escapeZplText(resolved, profile.encoding)
      text = escaped.value
      warnings.push(...escaped.warnings)
    }
    // stacked OR grid (grid falls back to stacked in Phase 1)
    const variantRegion =
      layout === 'columns'
        ? {
            xMm: zone.xMm + index * columnWidthMm,
            yMm: zone.yMm,
            wMm: columnWidthMm,
            hMm: zone.heightMm,
          }
        : {
            xMm: zone.xMm,
            yMm: zone.yMm + index * lineHeightMm,
            wMm: zone.widthMm,
            hMm: lineHeightMm,
          }
    const intrinsic = textIntrinsicSize({
      fontHeightMm,
      maxLines: 1,
      lineSpacingMm: 0,
      wrapBoxWidthMm: variantRegion.wMm,
    })
    const { anchorXMm } = resolveAnchor(variantRegion, intrinsic, placement)
    const anchorYMm = capBandAnchorY(
      variantRegion.yMm,
      variantRegion.hMm,
      fontHeightMm,
      placement.vAlign,
      A0_VMETRICS
    )
    plans.push({
      kind: 'text',
      id: `${c.id}:variant:${index}`,
      anchorXMm,
      anchorYMm,
      text,
      fontHeightMm,
      fontWidthMm: fontHeightMm / 2,
      fontId: c.fontId ?? '0',
      alignment: hAlignToPlan(placement.hAlign),
      wrapBoxWidthMm: variantRegion.wMm,
      maxLines: 1,
      lineSpacingMm: 0,
      // No `rotation` field on `LocalizedRichTextComponent` — hardcode 'N'
      // to match `planLocalizedRichText` in render-plan.ts.
      rotation: 'N',
      vAlign: placement.vAlign,
      regionXMm: zone.xMm,
      regionYMm: zone.yMm,
      regionWidthMm: zone.widthMm,
      regionHeightMm: zone.heightMm,
      regionBackgroundColor: zone.backgroundColor,
    })
  })
  return { plans, warnings }
}

const PLACEHOLDER_HRI: Record<'code-128' | 'ean-13' | 'qr' | 'gs1-128' | 'itf', boolean> = {
  'code-128': false,
  'ean-13': true,
  qr: false,
  'gs1-128': true,
  itf: true,
}

/**
 * Authoring counterpart of `planSymbol` (in `render-plan.ts`). Strategy:
 *   1. If the parent `buildAuthoringPlan` already got a barcode plan back
 *      from `buildRenderPlan` for this zone, return the zone's print plans
 *      verbatim (barcode + optional `:hri` text line) — parity with the
 *      print planner for resolved payloads.
 *   2. Otherwise synthesize a placeholder barcode plan whose `data` and
 *      `placeholderLabel` carry the bound `fieldKey` so the designer canvas
 *      shows a sized rect the user can select and edit. Defaults match the
 *      per-symbology defaults in `render-plan.ts:planSymbol`. No `:hri`
 *      text plan is synthesized — the placeholder badge already names the
 *      symbology and binding.
 *
 * Unsupported Phase-2 symbologies (datamatrix/pdf417/etc) render as a
 * `code-128` stand-in so `<RenderPlanItem>` has a real symbology to draw,
 * while `placeholderLabel` carries the truth ("datamatrix (unsupported)")
 * so the gap is obvious in the UI.
 *
 * Note: this helper does NOT call `buildRenderPlan` itself — the parent
 * computes it once for all zones and passes the per-zone plans in via
 * `printPlansForZone`. Warnings are accumulated by the parent.
 */
function planAuthoringSymbol(
  zone: FlatZone,
  c: SymbolComponent,
  profile: PrinterProfile,
  printPlansForZone: RenderPlan[]
): { plans: RenderPlan[]; warnings: Warning[] } {
  const warnings: Warning[] = []

  // If buildRenderPlan produced a real barcode plan for this zone, return
  // the zone's plans verbatim — parity with print mode for resolved
  // payloads. (Warnings were already accumulated by the parent.)
  if (printPlansForZone.some(p => p.kind === 'barcode')) {
    return { plans: printPlansForZone, warnings: [] }
  }

  // Derive the fieldKey for the placeholder label. Most data structures
  // carry a single `value: ValueSource` (`raw`/`gtin`/`url`); `gs1-ai` is
  // approximated by the first bound AI's fieldKey. Falls back to the
  // component id when nothing is bound.
  const fieldKey = extractFieldKeyForSymbol(c) ?? c.id
  const placeholder = `{${fieldKey}}`

  const placementSetting = c.placement ?? DEFAULT_PLACEMENT
  const region = { xMm: zone.xMm, yMm: zone.yMm, wMm: zone.widthMm, hMm: zone.heightMm }

  switch (c.symbology) {
    case 'code-128':
    case 'ean-13':
    case 'qr':
    case 'gs1-128':
    case 'itf': {
      // Full symbol box (quiet zones + bars + HRI band) for anchoring; the
      // plan's heightMm carries the quiet+bars box only — mirrors
      // `buildSymbolPlans` in render-plan.ts.
      const box = barcodeIntrinsicSize(c, placeholder, profile.dpi)
      const useIntrinsic = box.wMm > 0 && box.hMm > 0
      // Mirror render-plan.buildSymbolPlans: a barcode honors its own rotation
      // (R/B swap the on-label footprint) so the canvas preview matches print.
      const barRot = rotationToPlan(c.rotation)
      const footprint =
        barRot === 'R' || barRot === 'B'
          ? { wMm: box.hMm, hMm: box.wMm }
          : { wMm: box.wMm, hMm: box.hMm }
      const { anchorXMm, anchorYMm } = useIntrinsic
        ? resolveAnchor(region, footprint, placementSetting)
        : { anchorXMm: zone.xMm, anchorYMm: zone.yMm }
      return {
        plans: [
          {
            kind: 'barcode',
            id: c.id,
            anchorXMm,
            anchorYMm,
            widthMm: useIntrinsic ? box.wMm : zone.widthMm,
            heightMm: useIntrinsic ? box.hMm - box.hriBandMm : zone.heightMm,
            quietLeftMm: useIntrinsic ? box.quietLeftMm : 0,
            quietRightMm: useIntrinsic ? box.quietRightMm : 0,
            quietTopMm: useIntrinsic ? box.quietTopMm : 0,
            quietBottomMm: useIntrinsic ? box.quietBottomMm : 0,
            hriBandMm: useIntrinsic ? box.hriBandMm : 0,
            rotation: barRot,
            symbology: c.symbology,
            data: placeholder,
            placeholderLabel: placeholder,
            humanReadable: c.humanReadable ?? PLACEHOLDER_HRI[c.symbology],
            moduleWidthDots: Math.max(
              1,
              mmToDots(
                c.moduleWidthMm ?? SYMBOLOGY_METRICS[c.symbology].defaultModuleWidthMm,
                profile.dpi
              )
            ),
            heightDots: mmToDots(
              c.heightMm ?? SYMBOLOGY_METRICS[c.symbology].defaultHeightMm,
              profile.dpi
            ),
            regionXMm: zone.xMm,
            regionYMm: zone.yMm,
            regionWidthMm: zone.widthMm,
            regionHeightMm: zone.heightMm,
            regionBackgroundColor: zone.backgroundColor,
          },
        ],
        warnings,
      }
    }
    // Unsupported symbologies render as a code-128 stand-in so the user
    // sees a sized rect they can edit; the label names the requested
    // symbology so the gap is obvious.
    case 'code-39':
    case 'ean-8':
    case 'upc-a':
    case 'datamatrix':
    case 'pdf417':
    case 'aztec-cc': {
      return {
        plans: [
          {
            kind: 'barcode',
            id: c.id,
            anchorXMm: zone.xMm,
            anchorYMm: zone.yMm,
            widthMm: zone.widthMm,
            heightMm: zone.heightMm,
            symbology: 'code-128',
            data: placeholder,
            placeholderLabel: `${c.symbology} (unsupported)`,
            humanReadable: c.humanReadable ?? PLACEHOLDER_HRI['code-128'],
            rotation: 'N',
            moduleWidthDots: Math.max(
              1,
              mmToDots(SYMBOLOGY_METRICS['code-128'].defaultModuleWidthMm, profile.dpi)
            ),
            heightDots: mmToDots(
              c.heightMm ?? SYMBOLOGY_METRICS['code-128'].defaultHeightMm,
              profile.dpi
            ),
            regionXMm: zone.xMm,
            regionYMm: zone.yMm,
            regionWidthMm: zone.widthMm,
            regionHeightMm: zone.heightMm,
            regionBackgroundColor: zone.backgroundColor,
          },
        ],
        warnings,
      }
    }
    default: {
      const _ex: never = c.symbology
      throw new Error(`planAuthoringSymbol: unknown symbology ${JSON.stringify(_ex)}`)
    }
  }
}

/**
 * Authoring-mode image planner. Consumes the pre-computed print plan
 * (which always has `buffer: null` or a resolved buffer; buildRenderPlan
 * never silently skips images). Enriches with `placeholderLabel` when the
 * source is field-bound AND the buffer didn't resolve — this is the cue
 * `<RenderPlanItem>` uses to render `{fieldKey}` inside the dashed rect
 * instead of the cryptic "image unresolved" string.
 *
 * Static images (kind: 'url' | 'data-uri' | 'host-asset') and resolved
 * field-bound images return the print plan verbatim — deep-equal to
 * buildRenderPlan output.
 *
 * The `_zone` parameter is unused (the print plan already carries the
 * resolved anchor/size coordinates) but kept in the signature for symmetry
 * with the other `planAuthoring*` helpers.
 */
function planAuthoringImage(
  _zone: FlatZone,
  c: ImageComponent,
  printPlanForZone: RenderPlan | undefined
): { plans: RenderPlan[]; warnings: Warning[] } {
  if (!printPlanForZone || printPlanForZone.kind !== 'image') {
    // Defensive — buildRenderPlan always emits an image plan for image
    // primitives, so this branch should not run.
    return { plans: [], warnings: [] }
  }
  if (c.source.kind === 'field' && printPlanForZone.buffer === null) {
    return {
      plans: [
        {
          ...printPlanForZone,
          placeholderLabel: `{${c.source.fieldKey}}`,
        },
      ],
      warnings: [],
    }
  }
  return { plans: [printPlanForZone], warnings: [] }
}

/**
 * Find the bound fieldKey for a symbol's data structure, used to label the
 * placeholder. The contract's `SymbolDataStructure` discriminator is
 * `raw | gtin | gs1-ai | url`:
 *   - `raw` / `gtin` / `url` carry a single `value: ValueSource`. If that
 *     value is bound, return its fieldKey.
 *   - `gs1-ai` carries `elements: { ai, value }[]`. We approximate the
 *     composite by returning the first bound element's fieldKey.
 * Returns `undefined` when nothing is bound, so the caller can fall back
 * to the component id.
 */
function extractFieldKeyForSymbol(c: SymbolComponent): string | undefined {
  const ds = c.dataStructure

  const fromTextValue = (v: AnyValueSource): string | undefined => {
    if ('content' in v) {
      const m = SINGLE_VARIABLE_RE.exec(v.content)
      return m?.[1]
    }
    return v.kind === 'bound' ? v.fieldKey : undefined
  }

  switch (ds.kind) {
    case 'raw':
    case 'gtin':
    case 'url':
      return fromTextValue(ds.value as AnyValueSource)
    case 'gs1-ai':
      for (const el of ds.elements) {
        const key = fromTextValue(el.value as AnyValueSource)
        if (key !== undefined) return key
      }
      return undefined
    default: {
      const _ex: never = ds
      throw new Error(`extractFieldKeyForSymbol: unknown structure ${JSON.stringify(_ex)}`)
    }
  }
}
